<script setup>
  import { onMounted, onUnmounted, computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";

  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import CollapseExpandAllButton from "@/components/episto-ui/collapse-card/CollapseExpandAllButton.vue";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";

  import ContractsTable from "@/components/Contracts/ContractsTable.vue";
  import ContractFormDialog from "@/components/Contracts/ContractFormDialog.vue";
  import CreditManualDialog from "@/components/Contracts/CreditManualDialog.vue";

  import { useContractsStore } from "@stores/contracts";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { useContractsFilters } from "@utils/composables/filters/useContractsFilters";

  const contractsStore = useContractsStore();
  const currentUserStore = useCurrentUserStore();
  const { contracts, contractsCount, currentPage, searchQuery } = storeToRefs(contractsStore);
  const { isClient, isAdmin, isSale } = storeToRefs(currentUserStore);

  const canFilter = computed(() => isAdmin.value || isSale.value);

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear } = useContractsFilters();

  const isFormDialogOpen = ref(false);
  const isManualDialogOpen = ref(false);
  const editingContract = ref(null);
  const selectedSurvey = ref(null);
  const selectedContract = ref(null);
  const visibleContractIds = ref([]);

  const {
    isOpen: isDeleteDialogOpen,
    target: contractToDelete,
    requestDelete: openDeleteDialog,
    confirm: confirmDelete,
  } = useConfirmDelete(async (contract) => {
    await contractsStore.deleteContract(contract.id);
  });

  const canManage = computed(() => currentUserStore.isAdmin || currentUserStore.isSale);
  const pageTitle = computed(() => (isClient.value ? "My Contracts" : "Contracts"));

  watch(contracts, () => (visibleContractIds.value = []));

  const allExpanded = computed(
    () => contracts.value.length > 0 && contracts.value.every((c) => visibleContractIds.value.includes(c.id)),
  );

  const toggleContract = (id) => {
    if (visibleContractIds.value.includes(id)) {
      visibleContractIds.value = visibleContractIds.value.filter((cid) => cid !== id);
    } else {
      visibleContractIds.value.push(id);
    }
  };

  const toggleAll = () => {
    visibleContractIds.value = allExpanded.value ? [] : contracts.value.map((c) => c.id);
  };

  const openCreateDialog = () => {
    editingContract.value = null;
    isFormDialogOpen.value = true;
  };

  const openEditDialog = (contract) => {
    editingContract.value = contract;
    isFormDialogOpen.value = true;
  };

  const openManualDialog = ({ survey, contract }) => {
    selectedSurvey.value = survey;
    selectedContract.value = contract;
    isManualDialogOpen.value = true;
  };

  const refreshAfterManualSave = async () => {
    if (!selectedContract.value) return;
    await contractsStore.fetchOne(selectedContract.value.id);
  };

  const handlePageChange = (page) => contractsStore.fetch(page);

  onMounted(() => contractsStore.fetch(currentPage.value));
  onUnmounted(() => contractsStore.resetAllFilters());
</script>

<template>
  <IndexPageLayout
    :title="pageTitle"
    :action-label="canManage ? 'Add contract' : undefined"
    @action="openCreateDialog">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search by name or account">
        <template #filters>
          <FilterDropdown
            v-if="canFilter"
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            @toggle="handleFilterChange"
            @clear="handleClear" />
          <CollapseExpandAllButton
            v-if="contracts.length > 0"
            :expanded="allExpanded"
            @toggle="toggleAll" />
        </template>
      </IndexPageFilters>
    </template>

    <ContractFormDialog
      v-model:open="isFormDialogOpen"
      :contract="editingContract"
      @saved="contractsStore.fetch(currentPage)" />

    <CreditManualDialog
      v-model:open="isManualDialogOpen"
      :survey="selectedSurvey"
      @saved="refreshAfterManualSave" />

    <ContractsTable
      :contracts="contracts"
      :contracts-count="contractsCount"
      :current-page="currentPage"
      :expanded-ids="visibleContractIds"
      :can-manage="canManage"
      @toggle="toggleContract"
      @edit="openEditDialog"
      @delete="openDeleteDialog"
      @set-manual-credits="openManualDialog"
      @page-change="handlePageChange" />

    <ConfirmDialog
      v-model:open="isDeleteDialogOpen"
      title="Delete contract?"
      confirm-label="Delete"
      @confirm="confirmDelete">
      <span v-if="contractToDelete">
        <span class="font-semibold">{{ contractToDelete.name }}</span> will be permanently removed. This action cannot be undone.
      </span>
    </ConfirmDialog>
  </IndexPageLayout>
</template>
